/**
 * Sharing Service — API layer for playlist/station sharing between Maestro users.
 */

import { api } from './api';

export interface ShareResult {
  share_code: string;
  share_url: string;
  item_name: string;
  track_count: number | null;
}

export interface SharedItemPreview {
  share_code: string;
  item_type: 'playlist' | 'station';
  item_name: string;
  owner_name: string;
  track_count: number;
  preview_tracks: { title: string; artist: string }[];
  created_at: string;
  already_imported: boolean;
}

export interface ImportResult {
  success: boolean;
  item_type: 'playlist' | 'station';
  item_name: string;
  tracks_imported?: number;
  tracks_skipped?: number;
  skipped_reasons?: { title: string; artist: string; reason: string }[];
}

export interface MyShare {
  share_code: string;
  share_url: string;
  item_type: 'playlist' | 'station';
  item_name: string;
  access_count: number;
  created_at: string;
}

export interface CommunityItem {
  share_code: string;
  item_type: 'playlist' | 'station';
  item_name: string;
  owner_name: string;
  track_count: number | null;
  preview_tracks: { title: string; artist: string }[];
  import_count: number;
  created_at: string;
  already_imported: boolean;
}

export interface CommunityFeed {
  items: CommunityItem[];
  total: number;
  has_more: boolean;
}

export const sharingService = {
  async createShare(
    itemType: 'playlist' | 'station',
    itemId: string,
    shareToCommunity = false,
    options?: { description?: string; item_name?: string; item_data_override?: any }
  ): Promise<ShareResult> {
    return api.post('/api/shared', {
      item_type: itemType,
      item_id: itemId,
      share_to_community: shareToCommunity,
      ...(options?.description && { description: options.description }),
      ...(options?.item_name && { item_name: options.item_name }),
      ...(options?.item_data_override && { item_data_override: options.item_data_override }),
    });
  },

  async viewShare(code: string): Promise<SharedItemPreview> {
    return api.get(`/api/shared/${code}`);
  },

  async importShare(code: string): Promise<ImportResult> {
    return api.post(`/api/shared/${code}/import`);
  },

  async listMyShares(): Promise<MyShare[]> {
    return api.get('/api/shared/mine');
  },

  async deleteShare(code: string): Promise<{ success: boolean }> {
    return api.delete(`/api/shared/${code}`);
  },

  async getCommunityFeed(type = 'all', limit = 20, offset = 0): Promise<CommunityFeed> {
    return api.get(`/api/shared/community?type=${type}&limit=${limit}&offset=${offset}`);
  },
};
